import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Settings,
  LogOut,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { Mentor, DashboardStats } from '@/types/mentor';
import { AccessibilityButton } from '@/components/AccessibilityButton';

export default function MentorDashboardScreen() {
  const router = useRouter();
  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user?.email) return;

      const { data: mentorData } = await supabase
        .from('mentors')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();

      if (mentorData) {
        setMentor(mentorData);
      }

      await loadStats(user.email);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (mentorEmail: string) => {
    try {
      const { data: courses } = await supabase
        .from('processed_raw_courses')
        .select('course_id')
        .eq('mentor_email', mentorEmail);

      if (!courses || courses.length === 0) {
        setStats({
          totalLeads: 0,
          pendingLeads: 0,
          contactedLeads: 0,
          completedLeads: 0,
          leadsThisWeek: 0,
          leadsThisMonth: 0,
          leadsByDay: [],
        });
        return;
      }

      const courseIds = courses.map(c => c.course_id);

      const { data: leads } = await supabase
        .from('leads')
        .select('*')
        .in('course_id', courseIds)
        .order('created_at', { ascending: false });

      if (leads) {
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        const leadsThisWeek = leads.filter(
          (l) => new Date(l.created_at) >= weekAgo
        ).length;

        const leadsThisMonth = leads.filter(
          (l) => new Date(l.created_at) >= monthAgo
        ).length;

        const leadsByDay: { date: string; count: number }[] = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const dateStr = date.toISOString().split('T')[0];
          const count = leads.filter(
            (l) => l.created_at.split('T')[0] === dateStr
          ).length;
          leadsByDay.push({ date: dateStr, count });
        }

        setStats({
          totalLeads: leads.length,
          pendingLeads: leads.filter((l) => l.status === 'pending').length,
          contactedLeads: leads.filter((l) => l.status === 'contacted').length,
          completedLeads: leads.filter((l) => l.status === 'completed').length,
          leadsThisWeek,
          leadsThisMonth,
          leadsByDay,
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.replace('/');
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>טוען נתונים...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View>
          <Text
            style={styles.greeting}
            {...(Platform.OS === 'web' && {
              role: 'heading',
              'aria-level': 1,
            } as any)}
          >
            שלום, {mentor?.full_name || 'מנטור'}
          </Text>
          <Text style={styles.subGreeting}>ברוך הבא לדאשבורד שלך</Text>
        </View>
        <Pressable
          onPress={handleLogout}
          style={styles.logoutButton}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="התנתק מהמערכת"
          {...(Platform.OS === 'web' && {
            'aria-label': 'התנתק מהמערכת',
          } as any)}
        >
          <View aria-hidden={true}>
            <LogOut color={theme.colors.textLight} size={24} />
          </View>
        </Pressable>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.statsGrid}>
          <View
            style={[styles.statCard, styles.statCardPrimary]}
            accessible={true}
            accessibilityLabel={`${stats?.pendingLeads || 0} לידים ממתינים`}
          >
            <AlertCircle color={theme.colors.primary} size={32} aria-hidden={true} />
            <Text style={styles.statValue}>{stats?.pendingLeads || 0}</Text>
            <Text style={styles.statLabel}>לידים ממתינים</Text>
          </View>

          <View
            style={styles.statCard}
            accessible={true}
            accessibilityLabel={`${stats?.contactedLeads || 0} לידים בטיפול`}
          >
            <Clock color="#FFA500" size={32} aria-hidden={true} />
            <Text style={styles.statValue}>{stats?.contactedLeads || 0}</Text>
            <Text style={styles.statLabel}>בטיפול</Text>
          </View>

          <View
            style={styles.statCard}
            accessible={true}
            accessibilityLabel={`${stats?.completedLeads || 0} לידים הושלמו`}
          >
            <CheckCircle color="#4CAF50" size={32} aria-hidden={true} />
            <Text style={styles.statValue}>{stats?.completedLeads || 0}</Text>
            <Text style={styles.statLabel}>הושלמו</Text>
          </View>

          <View
            style={styles.statCard}
            accessible={true}
            accessibilityLabel={`${stats?.leadsThisWeek || 0} לידים השבוע`}
          >
            <TrendingUp color={theme.colors.primary} size={32} aria-hidden={true} />
            <Text style={styles.statValue}>{stats?.leadsThisWeek || 0}</Text>
            <Text style={styles.statLabel}>השבוע</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>7 הימים האחרונים</Text>
          <View style={styles.chartContainer}>
            {stats?.leadsByDay.map((day, index) => (
              <View key={index} style={styles.chartBar}>
                <View
                  style={[
                    styles.chartBarFill,
                    {
                      height: `${Math.max(day.count * 20, 5)}%`,
                    },
                  ]}
                />
                <Text style={styles.chartLabel}>{day.count}</Text>
                <Text style={styles.chartDate}>
                  {new Date(day.date).getDate()}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.actionsGrid}>
          <Pressable
            style={({ pressed }) => [
              styles.actionCard,
              pressed && styles.actionCardPressed,
            ]}
            onPress={() => router.push('/mentor/leads')}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="ניהול לידים - צפייה וטיפול בכל הלידים שלך"
            {...(Platform.OS === 'web' && {
              'aria-label': 'ניהול לידים - צפייה וטיפול בכל הלידים שלך',
            } as any)}
          >
            <Users color={theme.colors.primary} size={36} aria-hidden={true} />
            <Text style={styles.actionTitle}>ניהול לידים</Text>
            <Text style={styles.actionDescription}>
              צפייה וטיפול בכל הלידים שלך
            </Text>
          </Pressable>

          <Pressable
            style={({ pressed }) => [
              styles.actionCard,
              pressed && styles.actionCardPressed,
            ]}
            onPress={() => router.push('/mentor/courses')}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="הקורסים שלי - ניהול ועריכת הקורסים"
            {...(Platform.OS === 'web' && {
              'aria-label': 'הקורסים שלי - ניהול ועריכת הקורסים',
            } as any)}
          >
            <BookOpen color={theme.colors.primary} size={36} aria-hidden={true} />
            <Text style={styles.actionTitle}>הקורסים שלי</Text>
            <Text style={styles.actionDescription}>
              ניהול ועריכת הקורסים
            </Text>
          </Pressable>

          <Pressable
            style={({ pressed }) => [
              styles.actionCard,
              pressed && styles.actionCardPressed,
            ]}
            onPress={() => router.push('/mentor/settings')}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="הגדרות - עריכת פרופיל והגדרות"
            {...(Platform.OS === 'web' && {
              'aria-label': 'הגדרות - עריכת פרופיל והגדרות',
            } as any)}
          >
            <Settings color={theme.colors.primary} size={36} aria-hidden={true} />
            <Text style={styles.actionTitle}>הגדרות</Text>
            <Text style={styles.actionDescription}>
              עריכת פרופיל והגדרות
            </Text>
          </Pressable>
        </View>
      </ScrollView>

      <View style={styles.bottomNav}>
        <Pressable style={styles.navItem}>
          <LayoutDashboard color={theme.colors.primary} size={24} />
          <Text style={[styles.navText, styles.navTextActive]}>דאשבורד</Text>
        </Pressable>

        <Pressable
          style={styles.navItem}
          onPress={() => router.push('/mentor/leads')}
        >
          <Users color={theme.colors.textLight} size={24} />
          <Text style={styles.navText}>לידים</Text>
        </Pressable>

        <Pressable
          style={styles.navItem}
          onPress={() => router.push('/mentor/courses')}
        >
          <BookOpen color={theme.colors.textLight} size={24} />
          <Text style={styles.navText}>קורסים</Text>
        </Pressable>

        <Pressable
          style={styles.navItem}
          onPress={() => router.push('/mentor/settings')}
        >
          <Settings color={theme.colors.textLight} size={24} />
          <Text style={styles.navText}>הגדרות</Text>
        </Pressable>
      </View>

      <AccessibilityButton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.md,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.md,
    backgroundColor: theme.colors.backgroundLight,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  greeting: {
    fontSize: 24,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  subGreeting: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    marginTop: 2,
  },
  logoutButton: {
    padding: theme.spacing.xs,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    paddingBottom: 100,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.xl,
  },
  statCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    alignItems: 'center',
    gap: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  statCardPrimary: {
    borderColor: theme.colors.primary,
    borderWidth: 2,
  },
  statValue: {
    fontSize: 32,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  statLabel: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
  },
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  },
  chartContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 150,
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  chartBar: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '100%',
  },
  chartBarFill: {
    width: '80%',
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.sm,
    minHeight: 4,
  },
  chartLabel: {
    fontSize: 12,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    marginTop: theme.spacing.xs,
  },
  chartDate: {
    fontSize: 10,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    marginTop: 2,
  },
  actionsGrid: {
    gap: theme.spacing.md,
  },
  actionCard: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.border,
    gap: theme.spacing.sm,
  },
  actionCardPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.8,
  },
  actionTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  actionDescription: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    lineHeight: 20,
  },
  bottomNav: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    backgroundColor: theme.colors.backgroundLight,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
  },
  navItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
    gap: 4,
  },
  navText: {
    fontSize: 12,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  navTextActive: {
    color: theme.colors.primary,
  },
});
